import type { MouseEvent, ReactNode } from 'react';
import { useLocation, useNavigate, type To } from 'react-router-dom';
import { Group, Text } from '@mantine/core';

import RouterLink from './RouterLink';

import ChevronLeft from '@/assets/icons/content/chevron-left.svg';

interface BackLinkProps {
  /** Fallback destination, used when the page was opened directly (no in-app history). */
  to: To;
  children: ReactNode;
  /** When true, always navigate to `to` instead of popping history. */
  direct?: boolean;
}

/**
 * Detail-page "back" control. A plain left click pops in-app history so the
 * previous page's URL state (filters, sort, pagination) is restored; when there
 * is no history to pop (the page was opened via a direct link), it navigates to
 * `to`. Rendering a real link to `to` keeps modifier/right click able to open
 * the fallback target.
 */
const BackLink = ({ to, children, direct = false }: BackLinkProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (direct) return;

    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    if (location.key !== 'default') {
      event.preventDefault();
      navigate(-1);
    }
  };

  return (
    <RouterLink to={to} onClick={handleClick}>
      <Group gap={4} align="center" wrap="nowrap">
        <ChevronLeft width={18} height={18} />
        <Text span size="sm">
          {children}
        </Text>
      </Group>
    </RouterLink>
  );
};

export default BackLink;
